import React from 'react';
import './ReportQueueModule.scss';
import { useQueueContext } from '../../Queue/QueueContext';
import { QueuePanelProvider, QueuePanelSystem } from '../../Queue/common/QueuePanel';
import { BrowserPanel } from '../../KSQ2/Panels/BrowserPanel';
import { ConversationPanel } from '../../KSQ2/Panels/ConversationPanel';
import { IconNavQueue } from 'components/Icons';
import type { AskRequest } from '@hone-automation/common';
import classNames from 'classnames';
import { useRightPanel } from '../../NewTable/RightPanel/RightPanelContext';
import { useFlagCreationStore } from 'hooks/useFlagCreationStore';
import { useNTUtilsContext } from '../../NewTable/NTUtilsContext';
import { useReportsStore } from 'hooks/useReportsStore';
import FlagCreationPanel from './FlagCreationPanel';

interface ReportQueueModuleProps {
  data?: {
    requests?: AskRequest[];
    reportId?: string | null;
    reportType?: string;
    columnDates?: Array<{ start: string; end: string }>;
  };
  onRequestSelect?: (request: AskRequest | null) => void;
}

const ReportQueueContent = ({
  reportId,
  onRequestSelect,
}: {
  reportId?: string | null;
  onRequestSelect?: (request: AskRequest | null) => void;
}) => {
  const { state, dispatch } = useQueueContext();
  const { selectedItem, requests } = state;
  const { isExpanded, setActiveModuleId, setPanelVisible } = useRightPanel();
  const { pendingFlag, clearPendingFlag } = useFlagCreationStore();
  const { actions, setActions } = useNTUtilsContext();
  const setReportQueue = useReportsStore(state => state.setReportQueue);

  const ensurePanelOpen = React.useCallback(
    (moduleId: string) => {
      setActions({ ...actions, detailsPanel: true });
      setActiveModuleId(moduleId);
      setPanelVisible(true);
    },
    [actions, setActions, setActiveModuleId, setPanelVisible]
  );

  const handleFlagBackToChart = React.useCallback(() => {
    clearPendingFlag();
    setReportQueue(false);
    ensurePanelOpen('chart');
  }, [clearPendingFlag, setReportQueue, ensurePanelOpen]);

  const handleFlagBackToQueue = React.useCallback(() => {
    clearPendingFlag();
    setReportQueue(true);
    ensurePanelOpen('report-queue');
  }, [clearPendingFlag, setReportQueue, ensurePanelOpen]);

  const handleFlagBackToQueueList = React.useCallback(() => {
    dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
    handleFlagBackToQueue();
  }, [dispatch, handleFlagBackToQueue]);

  const isExistingFlag = Boolean(
    pendingFlag?.existingFlagId ||
      pendingFlag?.transaction?.flagged ||
      (pendingFlag?.trxFlagged && pendingFlag.trxFlagged.length > 0)
  );

  // Scroll/highlight the corresponding table cell when a request is selected.
  // Skip the initial mount — only react to user-initiated selection changes.
  const hasMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    onRequestSelect?.(selectedItem ?? null);
  }, [selectedItem, onRequestSelect]);

  return (
    <div
      className={classNames('report-queue-panel', 'right-panel-module', {
        'report-queue-panel--has-selection': !!selectedItem,
        'report-queue-panel--expanded': isExpanded,
        'report-queue-panel--flag-creation': !!pendingFlag,
      })}
    >
      {pendingFlag ? (
        <FlagCreationPanel
          pendingFlag={pendingFlag}
          reportId={reportId ?? undefined}
          onBack={isExistingFlag ? handleFlagBackToQueueList : handleFlagBackToChart}
          onDone={handleFlagBackToQueue}
        />
      ) : (
        <>
          <div className="report-queue-panel__browser dashboard-browser-panel">
            <BrowserPanel showBlockersInLists disableAutoSelect disableUrlSync />
          </div>
          {(isExpanded || selectedItem) && (
            <div className="report-queue-panel__conversation dashboard-conversation-panel">
              {selectedItem ? (
                <ConversationPanel
                  request={selectedItem}
                  comments={selectedItem.comments}
                  reportId={reportId ?? undefined}
                  onBackToList={isExpanded ? undefined : () => dispatch({ type: 'SET_SELECTED_ITEM', payload: null })}
                />
              ) : (
                <div className="report-queue-panel__empty dashboard-empty-state">
                  <IconNavQueue />
                  <span>
                    {requests.length > 0 ? 'Select a request to view details' : 'No requests for this report yet'}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
      <QueuePanelSystem />
    </div>
  );
};

const ReportQueueModule: React.FC<ReportQueueModuleProps> = ({ data, onRequestSelect }) => {
  return (
    <QueuePanelProvider>
      <ReportQueueContent reportId={data?.reportId ?? undefined} onRequestSelect={onRequestSelect} />
    </QueuePanelProvider>
  );
};

export default ReportQueueModule;
